<template>
  <div>
    <s-loader
      v-if="loading"
      class="my-5"
      size="lg"
    />
    <b-container
      fluid
      class="pt-2"
      v-if="!!isSensorComponentized"
    >
      <group-componentized-module
        :options="sensorInformation"
        :selected-information="selectedInformation"
        @on-change-selected-information="onChangeSelectedInformationHandler"
      />
    </b-container>
    <s-table
      v-if="!loading"
      class="configuration__table"
      local-sorting
      :items="contacts"
      :fields="fieldsHandler"
      :busy="loading"
    >
      <template #cell(registerStatus)="data">
        <span class="material-icons checkbox-icon">
          {{
          isActiveContact(data.item.registerStatus)
            ? 'check_box'
            : 'check_box_outline_blank'
        }}
        </span>
      </template>
      <template #cell(alarmInitialStatus)="data">
        {{ $t(`enums.initial-state.${data.item.alarmInitialStatus}`) }}
      </template>
      <template #cell(activateAlarm)="data">
        <span
          v-if="isActiveContact(data.item.registerStatus)"
          class="material-icons"
          :class="activeAlarmVisualization(data).class"
        >
          {{ activeAlarmVisualization(data).icon }}
        </span>
      </template>
      <template #cell(actions)="data">
        <b-button
          class="configuration__table-buttons-edit"
          v-if="hasSupervisorPermission"
          variant="outline-primary"
          size="sm"
          @click="openEditModal(data.item)"
        >
          <s-icon name="edit" />
          {{ $t('actions.edit') }}
        </b-button>
      </template>
    </s-table>
    <contact-modal
      :active="showModalContact"
      :editing-contact="editingContact"
      :sensor-id="selectedAssetModuleId"
      :is-loading="loading"
      :used-names="usedNames"
      @close="closeModalContact"
      @submit="submitContact"
    />
  </div>
</template>

<script>
import { InstallationStatus, ModuleCategorys } from 'sigma-mappings';
import ContactService from '@/domain/services/installation/supervision/sensors/contact';
import eventBus from '@/services/globalEventBus';
import event from '@/domain/events/engineeringModulesHeader';
import ContactModal from '@/domain/components/installation/supervision/sensors/configurationDM1/ContactModal';
import GroupComponentizedModule from '@/domain/components/installation/supervision/sensors/GroupComponentizedModule';

export default {
  name: 'ConfigurationDM1',
  components: {
    ContactModal,
    GroupComponentizedModule,
  },
  props: {
    category: String,
    isSensorComponentized: {
      type: Boolean,
      default: false,
    },
    selectedInformation: {
      type: Object,
      default: () => ({}),
    },
    sensorInformation: {
      type: Array,
      default: () => ([]),
    },
  },
  data: () => ({
    showModalContact: false,
    editingContact: null,
    contacts: [],
    loading: true,
    usedNames: [],
  }),
  methods: {
    activeAlarmVisualization(data) {
      return data.item.activateAlarm
        ? { icon: 'visibility', class: 'alarm-active' }
        : { icon: 'visibility_off', class: 'alarm-inactive' };
    },
    isActiveContact(activeContact) {
      return activeContact === InstallationStatus.ACTIVE;
    },
    openEditModal(contact) {
      this.editingContact = contact.id;
      this.usedNames = (this.contacts || [])
        .filter(x => x.id !== contact.id && !!x.description)
        .map(x => x.description.trim().toLowerCase());
      this.showModalContact = true;
    },
    closeModalContact() {
      this.showModalContact = false;
    },
    confirmExclusion(contactId) {
      this.$confirmMessage({ code: 'MC011' })
        .accepted(() => {
          this.deleteContact(contactId);
        });
    },
    deleteContact(contactId) {
      this.loading = true;

      ContactService.deleteContact(contactId)
        .then(() => {
          this.$pushAlert({ code: 'MS007' });
          this.loadContacts();
        })
        .catch(() => {
          this.loading = false;
        });
    },
    submitContact(contact) {
      this.loading = true;

      ContactService.updateContact(contact)
        .then(() => {
          this.$pushAlert({ code: 'MS006' });
          this.loadContacts();
          this.closeModalContact();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    refreshDataEvent({ wait }) {
      wait(this.loadContacts());
    },
    loadContacts() {
      this.loading = true;

      this.contacts = [];
      return ContactService.getContactsBySensor(this.selectedAssetModuleId)
        .then((contacts) => {
          this.contacts = contacts;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onChangeSelectedInformationHandler(value) {
      this.$emit('on-change-selected-information', value);
    },
  },
  computed: {
    fieldsHandler() {
      return this.hasSupervisorPermission ? this.supervisorFields : this.userFields;
    },
    userFields() {
      return [
        {
          key: 'name',
          sortable: false,
          label: this.$t('sensors.configuration.contact'),
        },
        {
          key: 'description',
          sortable: false,
          label: this.$t('sensors.configuration.description'),
        },
        {
          key: 'registerStatus',
          sortable: false,
          label: this.$t('sensors.configuration.active-contact'),
          class: 'text-center',
        },
        {
          key: 'alarmInitialStatus',
          sortable: false,
          label: this.$t('sensors.configuration.start-position'),
        },
        {
          key: 'activateAlarm',
          sortable: false,
          label: this.$t('sensors.configuration.alarm'),
          class: 'text-center',
        },
      ];
    },
    supervisorFields() {
      return [
        ...this.userFields,
        {
          key: 'actions',
          sortable: false,
          class: 'pr-5',
          label: '',
        },
      ];
    },
    hasSupervisorPermission() {
      return this.$hasUserPermission('PER015');
    },
    selectedAssetModuleId() {
      const { sensorId } = this.$route.params;
      const { assetModuleId } = this.selectedInformation || {};
      return this.category === ModuleCategorys.COMPONENTIZED
        ? assetModuleId
        : sensorId;
    },
  },
  created() {
    this.loadContacts();
    eventBus.on(event.refresh, this.refreshDataEvent, this);
  },
  beforeDestroy() {
    eventBus.off(event.refresh, this.refreshDataEvent, this);
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/variables"
@import "src/styles/defaults/mobile-first"

.checkbox-icon
  color: var(--font-color-secondary)

.alarm
  &-active
    color: var(--font-color-secondary)
  &-inactive
    color: var(--font-color-tertiary)

.configuration
  &__table-buttons
    width: 6rem
    margin-top: 3px

    &:hover, &-edit:hover
      color: var(--color-page-background)

    &-edit
      white-space: nowrap

  &__table ::v-deep
    @media not print
      max-height: 720px

    tbody tr td
      word-break: break-word

  ::v-deep &__cell
    &-check
      width: 40px

  &__alarm
    color: var(--font-color-primary)
    pointer-events: none
    min-width: initial !important
    cursor: default !important
    width: 2.2rem
    padding: 2px 0
    font-size: 1.1rem
</style>
